import * as z from 'zod';

export const QueryAvailabilityRequest = z.object({
  query: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object. For a list of\nfields you can filter by, see [Availability Calendar supported filters](https://dev.wix.com/docs/rest/business-solutions/bookings/time-slots/availability-calendar/supported-fields-filters-and-sorting).\nYou must include `serviceId`, `startDate` and `endDate` in the filter. This avoids large results that can impact performance.'
        ),
      sort: z
        .array(
          z.object({
            fieldName: z
              .string()
              .describe('Name of the field to sort by.')
              .max(512)
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .describe(
      'Query options. Refer to the\n[supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/availability-calendar/supported-fields-filters-and-sorting)\nfor a complete list of supported filters.'
    ),
  options: z
    .object({
      timezone: z
        .string()
        .describe(
          'Time zone override for the `endDate` and `startDate` filters, in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database), such as\n`America/New_York` or `UTC`.\n\nFor example, if you specify `("startDate": "2025-11-25T17:00:00+01:00"}` (note\nthe +01:00 offset) in combination with `{"timezone": "Europe/Bucharest"}`\n(+02:00 offset), *Query Availability* adjusts the effective start time to\nreflect the specified time zone. In this case, the start time in UTC would\nbe `2025-11-25T15:00:00` (using the +02:00 offset).\n\nLearn more about [handling Daylight Savings Time](https://dev.wix.com/api/rest/wix-bookings/availability-calendar/query-availability#wix-bookings_availability-calendar_query-availability_daylight-savings-time-dst).\n\nDefault: No modification applied to the `endDate` and `startDate` filters.'
        )
        .optional()
        .nullable(),
      slotsPerDay: z
        .number()
        .int()
        .describe(
          'Maximum number of appointment slots to return for each date. For example, if\nyou specify `{"slotsPerDay": 3}`, a maximum of 3 appointment slots/class events is\nreturned for each day that\'s within the query filter\'s date range.'
        )
        .optional()
        .nullable(),
    })
    .describe('Options to use for querying availability.')
    .optional(),
});
export const QueryAvailabilityResponse = z.object({
  availabilityEntries: z
    .array(
      z.object({
        slot: z
          .object({
            sessionId: z
              .string()
              .describe(
                "ID for the slot's corresponding session, when the session is either a single session\nor a specific session generated from a recurring session.\n\nDeprecated. Please use `eventId` instead."
              )
              .optional()
              .nullable(),
            serviceId: z.string().describe('Service ID.').optional(),
            scheduleId: z.string().describe('Schedule ID.').optional(),
            startDate: z
              .string()
              .describe(
                'The start time of this slot in [RFC 3339](https://www.rfc-editor.org/rfc/rfc3339)\nformat.\n\nIf `timezone` is specified,\ndates are based on the local date/time. This means that the timezone offset\nin the `start_date` is ignored.'
              )
              .optional()
              .nullable(),
            endDate: z
              .string()
              .describe(
                'The end time of this slot in\n[RFC 3339](https://www.rfc-editor.org/rfc/rfc3339) format.\n\nIf `timezone` is specified,\ndates are based on the local date/time. This means that the timezone offset\nin the `end_date` is ignored.'
              )
              .optional()
              .nullable(),
            timezone: z
              .string()
              .describe(
                'The timezone for which slot availability is to be calculated.\n\nLearn more about [handling Daylight Savings Time (DST) for local time zones](https://dev.wix.com/api/rest/wix-bookings/availability-calendar/query-availability#wix-bookings_availability-calendar_query-availability_handling-daylight-savings-time-dst-for-local-time-zones)\nwhen calculating availability.'
              )
              .optional()
              .nullable(),
            resource: z
              .object({
                _id: z
                  .string()
                  .describe('Resource ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Resource name. Read only.')
                  .max(1200)
                  .optional()
                  .nullable(),
              })
              .describe(
                'The resource required for this slot. Currently, the only supported resource\nis the relevant staff member for the slot.'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Location name.')
                  .optional()
                  .nullable(),
                formattedAddress: z
                  .string()
                  .describe('The full address of this location.')
                  .optional()
                  .nullable(),
                formattedAddressTranslated: z
                  .string()
                  .describe('The full translated address of this location.')
                  .max(512)
                  .optional()
                  .nullable(),
                locationType: z
                  .enum([
                    'UNDEFINED',
                    'OWNER_BUSINESS',
                    'OWNER_CUSTOM',
                    'CUSTOM',
                  ])
                  .describe('Location type.')
                  .optional(),
              })
              .describe('Geographic location of the slot.')
              .optional(),
            eventId: z
              .string()
              .describe(
                "ID for the slot's corresponding event, when the event is either a single event\nor a specific event generated from a recurring event."
              )
              .min(36)
              .max(250)
              .optional()
              .nullable(),
          })
          .describe(
            'The slot for the corresponding session, when the session is either a single session\nor a specific session generated from a recurring session.'
          )
          .optional(),
        bookable: z
          .boolean()
          .describe(
            "Whether the slot is bookable. Bookability is determined by checking a\nsession's open slots and booking policies. Locks are not taken into\naccount."
          )
          .optional(),
        totalSpots: z
          .number()
          .int()
          .describe(
            'Total number of spots for this slot.\nFor example, if a session has a total of 10 spots and 3 spots are booked,\n`spotsTotal` is 10 and `openSpots` is 7.'
          )
          .optional()
          .nullable(),
        openSpots: z
          .number()
          .int()
          .describe('Number of open spots for this slot.')
          .optional()
          .nullable(),
        waitingList: z
          .object({
            totalSpots: z
              .number()
              .int()
              .describe(
                'Total number of spots and open spots for this waitlist.\nFor example, a Yoga class with 10 waitlist spots and 3 registered\non the waitlist has 10 `total_spots` and 7 `open_spots`.'
              )
              .optional()
              .nullable(),
            openSpots: z
              .number()
              .int()
              .describe('Number of open spots for this waitlist.')
              .optional()
              .nullable(),
          })
          .describe(
            "An object describing the slot's waitlist and its occupancy."
          )
          .optional(),
        bookingPolicyViolations: z
          .object({
            tooEarlyToBook: z
              .boolean()
              .describe(
                'Bookings policy violation. Too early to book this slot.'
              )
              .optional()
              .nullable(),
            tooLateToBook: z
              .boolean()
              .describe(
                'Bookings policy violation. Too late to book this slot.'
              )
              .optional()
              .nullable(),
            bookOnlineDisabled: z
              .boolean()
              .describe(
                'Bookings policy violation. Online booking is disabled for this slot.'
              )
              .optional()
              .nullable(),
            validSubmissionRequired: z
              .boolean()
              .describe(
                'Bookings policy violation. Intake Form submission is required for this slot.'
              )
              .optional()
              .nullable(),
          })
          .describe('Booking policy violations for the slot.')
          .optional(),
        locked: z
          .boolean()
          .describe(
            'Indicates whether the slot is locked because a waitlist exists.\nWhen a slot frees up, the slot is offered to the next customer on the waitlist. Read-only.'
          )
          .optional()
          .nullable(),
        isFromV2: z
          .boolean()
          .describe('Deprecated. not in use since 2025-06-15.')
          .optional(),
      })
    )
    .optional(),
});
export const GetScheduleAvailabilityRequest = z.object({
  scheduleId: z
    .string()
    .describe('The schedule ID for which availability is being checked.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetScheduleAvailabilityResponse = z.object({
  availability: z
    .object({
      totalSpots: z
        .number()
        .int()
        .describe(
          'The total number of spots defined for the schedule, including\nboth open and non-available spots.'
        )
        .optional()
        .nullable(),
      openSpots: z
        .number()
        .int()
        .describe('The number of open spots defined for the schedule.')
        .optional()
        .nullable(),
      bookingPolicyViolations: z
        .object({
          tooEarlyToBook: z
            .boolean()
            .describe('Bookings policy violation. Too early to book this slot.')
            .optional()
            .nullable(),
          tooLateToBook: z
            .boolean()
            .describe('Bookings policy violation. Too late to book this slot.')
            .optional()
            .nullable(),
          bookOnlineDisabled: z
            .boolean()
            .describe(
              'Bookings policy violation. Online booking is disabled for this slot.'
            )
            .optional()
            .nullable(),
          validSubmissionRequired: z
            .boolean()
            .describe(
              'Bookings policy violation. Intake Form submission is required for this slot.'
            )
            .optional()
            .nullable(),
        })
        .describe('Booking policy violations for the schedule.')
        .optional(),
    })
    .optional(),
  bookingPolicySettings: z
    .object({
      maxParticipantsPerBooking: z
        .number()
        .int()
        .describe(
          'The policy defining the maximum number of participants that can\nbe booked for a slot or a schedule.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CalculateMultiSlotAvailabilityRequest = z.object({
  options: z
    .object({
      from: z.string().max(30).optional(),
      to: z.string().max(30).optional(),
      timeZone: z.string().max(30).optional(),
      bookable: z
        .boolean()
        .describe(
          'TODO good definition of what bookable means https://github.com/wix-private/scheduler/pull/18267/files?file-filters%5B%5D=.proto&show-viewed-files=true#r1199809006'
        )
        .optional()
        .nullable(),
      bookingPolicyViolations: z
        .object({
          tooEarlyToBook: z
            .boolean()
            .describe('Bookings policy violation. Too early to book this slot.')
            .optional()
            .nullable(),
          tooLateToBook: z
            .boolean()
            .describe('Bookings policy violation. Too late to book this slot.')
            .optional()
            .nullable(),
          bookOnlineDisabled: z
            .boolean()
            .describe(
              'Bookings policy violation. Online booking is disabled for this slot.'
            )
            .optional()
            .nullable(),
          validSubmissionRequired: z
            .boolean()
            .describe(
              'Bookings policy violation. Intake Form submission is required for this slot.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'each nested field is checked on its own. i.e. if `too_early_to_book` is defined and `too_late_to_book` is not defined\nwe will return slots for which `too_early_to_book` is same as on the request, regardless of `too_late_to_book`.'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z.string().describe('Location name.').optional().nullable(),
          formattedAddress: z
            .string()
            .describe('The full address of this location.')
            .optional()
            .nullable(),
          formattedAddressTranslated: z
            .string()
            .describe('The full translated address of this location.')
            .max(512)
            .optional()
            .nullable(),
          locationType: z
            .enum(['UNDEFINED', 'OWNER_BUSINESS', 'OWNER_CUSTOM', 'CUSTOM'])
            .optional(),
        })
        .describe(
          'support filtering by location type, or by locationId. Other fields like `name` are ignored\nmust be set, and must have locationType. If locationType is `OWNER_BUSINESS`, must have location_id'
        )
        .optional(),
      slots: z
        .array(
          z.object({
            serviceId: z
              .string()
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            resourcesFilter: z
              .object({ resourceIds: z.array(z.string()).optional() })
              .optional(),
            customerChoices: z
              .object({
                durationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Selected duration in minutes, based on a [service variant](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction) the customer chose.\nWhen using [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction), we recommend passing only `addOnIds` instead of `durationInMinutes`. Wix Bookings then calculates the total duration automatically. If you specify both `addOnIds` and `durationInMinutes`, `durationInMinutes` must equal the service duration plus all selected add-on durations.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)\nDefault: `15` minutes'
                  )
                  .min(1)
                  .max(44639)
                  .optional()
                  .nullable(),
                addOnIds: z.array(z.string()).max(21).optional(),
              })
              .describe(
                'will be passed to availability-2, and to the availability-constraints SPI'
              )
              .optional(),
          })
        )
        .min(1)
        .max(6)
        .optional(),
      slotsPerDay: z
        .number()
        .int()
        .describe(
          "Maximum number of slots to load for each date. For example, if `slots_per_day` is set to `3`,\nat most 3 available slots are returned for each day in the date range specified in the query's\n`filter`.\n\nWhen a day has both bookable and non-bookable slots, bookable slots are returned first.\nNon-bookable slots are returned according to the specified filters, after all\nbookable slots are already included."
        )
        .optional()
        .nullable(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .optional(),
    })
    .optional(),
});
export const CalculateMultiSlotAvailabilityResponse = z.object({
  slots: z
    .array(
      z.object({
        slot: z
          .object({
            sessionId: z
              .string()
              .describe(
                "ID for the slot's corresponding session, when the session is either a single session\nor a specific session generated from a recurring session.\n\nDeprecated. Please use `eventId` instead."
              )
              .optional()
              .nullable(),
            serviceId: z.string().describe('Service ID.').optional(),
            scheduleId: z.string().describe('Schedule ID.').optional(),
            startDate: z
              .string()
              .describe(
                'The start time of this slot in [RFC 3339](https://www.rfc-editor.org/rfc/rfc3339)\nformat.\n\nIf `timezone` is specified,\ndates are based on the local date/time. This means that the timezone offset\nin the `start_date` is ignored.'
              )
              .optional()
              .nullable(),
            endDate: z
              .string()
              .describe(
                'The end time of this slot in\n[RFC 3339](https://www.rfc-editor.org/rfc/rfc3339) format.\n\nIf `timezone` is specified,\ndates are based on the local date/time. This means that the timezone offset\nin the `end_date` is ignored.'
              )
              .optional()
              .nullable(),
            timezone: z
              .string()
              .describe(
                'The timezone for which slot availability is to be calculated.\n\nLearn more about [handling Daylight Savings Time (DST) for local time zones](https://dev.wix.com/api/rest/wix-bookings/availability-calendar/query-availability#wix-bookings_availability-calendar_query-availability_handling-daylight-savings-time-dst-for-local-time-zones)\nwhen calculating availability.'
              )
              .optional()
              .nullable(),
            resource: z
              .object({
                _id: z
                  .string()
                  .describe('Resource ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Resource name. Read only.')
                  .max(1200)
                  .optional()
                  .nullable(),
              })
              .describe(
                'The resource required for this slot. Currently, the only supported resource\nis the relevant staff member for the slot.'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Location name.')
                  .optional()
                  .nullable(),
                formattedAddress: z
                  .string()
                  .describe('The full address of this location.')
                  .optional()
                  .nullable(),
                formattedAddressTranslated: z
                  .string()
                  .describe('The full translated address of this location.')
                  .max(512)
                  .optional()
                  .nullable(),
                locationType: z
                  .enum([
                    'UNDEFINED',
                    'OWNER_BUSINESS',
                    'OWNER_CUSTOM',
                    'CUSTOM',
                  ])
                  .describe('Location type.')
                  .optional(),
              })
              .describe('Geographic location of the slot.')
              .optional(),
            eventId: z
              .string()
              .describe(
                "ID for the slot's corresponding event, when the event is either a single event\nor a specific event generated from a recurring event."
              )
              .min(36)
              .max(250)
              .optional()
              .nullable(),
          })
          .describe(
            'The slot for the corresponding session, when the session is either a single session\nor a specific session generated from a recurring session.'
          )
          .optional(),
        bookable: z
          .boolean()
          .describe(
            "Whether the slot is bookable. Bookability is determined by checking a\nsession's open slots and booking policies. Locks are not taken into\naccount."
          )
          .optional(),
        totalSpots: z
          .number()
          .int()
          .describe(
            'Total number of spots for this slot.\nFor example, if a session has a total of 10 spots and 3 spots are booked,\n`spotsTotal` is 10 and `openSpots` is 7.'
          )
          .optional()
          .nullable(),
        openSpots: z
          .number()
          .int()
          .describe('Number of open spots for this slot.')
          .optional()
          .nullable(),
        waitingList: z
          .object({
            totalSpots: z
              .number()
              .int()
              .describe(
                'Total number of spots and open spots for this waitlist.\nFor example, a Yoga class with 10 waitlist spots and 3 registered\non the waitlist has 10 `total_spots` and 7 `open_spots`.'
              )
              .optional()
              .nullable(),
            openSpots: z
              .number()
              .int()
              .describe('Number of open spots for this waitlist.')
              .optional()
              .nullable(),
          })
          .describe(
            "An object describing the slot's waitlist and its occupancy."
          )
          .optional(),
        bookingPolicyViolations: z
          .object({
            tooEarlyToBook: z
              .boolean()
              .describe(
                'Bookings policy violation. Too early to book this slot.'
              )
              .optional()
              .nullable(),
            tooLateToBook: z
              .boolean()
              .describe(
                'Bookings policy violation. Too late to book this slot.'
              )
              .optional()
              .nullable(),
            bookOnlineDisabled: z
              .boolean()
              .describe(
                'Bookings policy violation. Online booking is disabled for this slot.'
              )
              .optional()
              .nullable(),
            validSubmissionRequired: z
              .boolean()
              .describe(
                'Bookings policy violation. Intake Form submission is required for this slot.'
              )
              .optional()
              .nullable(),
          })
          .describe('Booking policy violations for the slot.')
          .optional(),
        locked: z
          .boolean()
          .describe(
            'Indicates whether the slot is locked because a waitlist exists.\nWhen a slot frees up, the slot is offered to the next customer on the waitlist. Read-only.'
          )
          .optional()
          .nullable(),
        isFromV2: z
          .boolean()
          .describe('Deprecated. not in use since 2025-06-15.')
          .optional(),
      })
    )
    .optional(),
  cursorPagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
